/**
 * TwoFactorOTP Component
 * Stateless 2FA verification using encrypted challenge tokens
 * 
 * Flow:
 * 1. User enters 6-digit OTP code
 * 2. Component retrieves challenge token from sessionStorage
 * 3. POST /verifyOTP with {challengeToken, otp}
 * 4. On 401: Server returns new token with incremented attempt counter
 * 5. On 200: Server sets JWT cookies, component redirects
 * 
 * Token Management:
 * - Challenge token is portable (JWE encrypted)
 * - Works across multiple pods (stateless)
 * - Expires in 5 minutes (server-side validation)
 * - Encrypted OTP code inside token (can't be tampered)
 */

import React, { useEffect, useRef, useState } from "react";
import { Typography, Button, CircularProgress } from "@material-ui/core";
import WelcomeHeader from "../_shared/welcome-header/WelcomeHeader";
import "./TwoFactorOTP.scss";
import SSO from "../../sso";
import config from "../../sso/config";
import { 
  retrieveChallengeToken,
  retrieveRemainingAttempts,
  updateChallengeToken,
  clearChallengeToken
} from "../../utils/2fa-token.service";
import {
  verifyOtp,
  resendOtp,
  getErrorMessage,
  getAttemptsMessage
} from "../../utils/2fa-api.service";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const DEFAULT_OTP_ATTEMPTS = 3;
const OTP_INPUT_KEYS = Array.from({ length: OTP_LENGTH }, (_, index) => `otp-digit-${index + 1}`);

const TwoFactorOTP = () => {
  /* OTP Input State */
  const [otpDigits, setOtpDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [isError, setError] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const otpInputRefs = useRef([]);

  /* Resend State */
  const [remainingSeconds, setRemainingSeconds] = useState(RESEND_COOLDOWN_SECONDS);
  const [isResending, setIsResending] = useState(false);
  const [hasResentOnce, setHasResentOnce] = useState(false);

  /* Challenge Token State (NEW for stateless 2FA) */
  const [challengeToken, setChallengeToken] = useState("");
  const [remainingAttempts, setRemainingAttempts] = useState(DEFAULT_OTP_ATTEMPTS);

  const otpCode = otpDigits.join("");

  /**
   * Load challenge token from sessionStorage on component mount
   * Challenge token was stored by PreLogin when redirecting to OTP page
   */
  useEffect(() => {
    const token = retrieveChallengeToken();
    const attempts = retrieveRemainingAttempts();

    if (token) {
      setChallengeToken(token);
      setRemainingAttempts(attempts);
    } else {
      /* No challenge token - user shouldn't be on this page */
      setError(true);
      setErrMsg('Session expired. Please login again.');
    }
  }, []);

  /**
   * Focus first OTP digit input on mount
   */
  useEffect(() => {
    if (otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, []);

  /**
   * Resend cooldown timer
   * Shows countdown before next resend is allowed
   */
  useEffect(() => {
    if (remainingSeconds <= 0 || hasResentOnce) {
      return undefined;
    }

    const timer = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [remainingSeconds, hasResentOnce]);

  /**
   * Focus a specific OTP input
   */
  const focusInput = (index) => {
    if (otpInputRefs.current[index]) {
      otpInputRefs.current[index].focus();
    }
  };

  /**
   * Update OTP digit at specific index
   */
  const updateDigitAtIndex = (index, nextValue) => {
    const updatedDigits = [...otpDigits];
    updatedDigits[index] = nextValue;
    setOtpDigits(updatedDigits);
  };

  /**
   * Handle OTP input change
   * - Accept only digits
   * - Auto-focus next input when digit entered
   */
  const handleOTPChange = (index, event) => {
    const nextDigit = event.target.value.replace(/[^0-9]/g, "").slice(-1);

    updateDigitAtIndex(index, nextDigit);
    setErrMsg("");
    setError(false);

    if (nextDigit && index < OTP_LENGTH - 1) {
      focusInput(index + 1);
    }
  };

  /**
   * Handle OTP input key down
   * - Backspace: Move focus to previous input
   */
  const handleOTPKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpDigits[index] && index > 0) {
      focusInput(index - 1);
    }
  };

  /**
   * Block paste operations for security
   */
  const blockOTPClipboardAction = (event) => {
    event.preventDefault();
  };

  /**
   * Validate OTP format before submission
   */
  const getOtpValidationError = (value) => {
    if (!value || value.trim().length === 0) {
      return "OTP code is required";
    }

    if (value.length !== 6) {
      return "OTP code must be 6 digits";
    }

    return "";
  };

  /**
   * Check if redirect URL is safe (prevents open redirect attacks)
   * Accepts both relative paths (/login) and absolute URLs (https://localhost:3000/login)
   */
  const isSafeInternalRedirect = (redirectURL) => {
    if (typeof redirectURL !== "string" || /^\s/.test(redirectURL)) {
      return false;
    }
    
    /* Relative URL: must start with / */
    if (/^\/(?!\/)/.test(redirectURL)) {
      return true;
    }
    
    /* Absolute URL: must be http(s) to same domain */
    try {
      const url = new URL(redirectURL, window.location.origin);
      return url.origin === window.location.origin;
    } catch {
      return false;
    }
  };

  /**
   * Handle successful OTP verification
   * - Clear challenge token from storage
   * - Redirect to URL provided by API (cookies already set by server)
   */
  const handleVerifyOtpSuccess = (responseData) => {
    clearChallengeToken();
    
    const redirectURL = responseData.redirectURL;
    if (isSafeInternalRedirect(redirectURL)) {
      window.location.href = redirectURL;
    } else {
      window.location.href = '/projects';
    }
  };

  /**
   * Handle OTP verification
   * POST /verifyOTP {challengeToken, otp}
   * 
   * Success (200):
   *   - JWT cookies set by server
   *   - Redirect to projects
   * 
   * Failed Attempt (401 with new token):
   *   - Update challenge token (incremented counter)
   *   - Show remaining attempts
   *   - Allow retry
   * 
   * Failed Attempt (401 without token):
   *   - Token expired
   *   - Redirect to login
   * 
   * Rate Limited (429):
   *   - Too many attempts
   *   - Wait and retry
   * 
   * Locked (423):
   *   - Exceeded max attempts
   *   - Contact support
   */
  const handleVerifyOTP = async () => {
    const otpValidationError = getOtpValidationError(otpCode);
    if (otpValidationError) {
      setError(true);
      setErrMsg(otpValidationError);
      return;
    }

    if (!challengeToken) {
      setError(true);
      setErrMsg("Session expired. Please login again.");
      return;
    }

    setIsLoading(true);

    try {
      /* Call API utility which includes challenge token */
      const response = await verifyOtp(otpCode);

      if (response && response.status === 200) {
        /* Success - JWT cookies set by server, redirect */
        handleVerifyOtpSuccess(response);
      } else if (response && response.status === 401 && response.challengeToken) {
        /* Failed attempt with new token - update and allow retry */
        updateChallengeToken(response.challengeToken, response.remainingAttempts);
        setChallengeToken(response.challengeToken);
        setRemainingAttempts(response.remainingAttempts || DEFAULT_OTP_ATTEMPTS);
        
        setError(true);
        setErrMsg(response.error || 'Invalid OTP. Please try again.');
      } else {
        /* Other errors (429, 423, 401 without token, etc.) */
        setError(true);
        setErrMsg(getErrorMessage(response?.status, response?.error));
        
        /* If token is missing on 401, user session expired */
        if (response?.status === 401) {
          setTimeout(() => {
            window.location = '/preLogin';
          }, 2000);
        }
      }
    } catch (err) {
      setError(true);
      setErrMsg("Error verifying OTP. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle back to login
   * Clear challenge token and redirect to preLogin
   */
  const handleBackToLogin = () => {
    clearChallengeToken();
    window.location = "/preLogin";
  };

  /**
   * Handle OTP resend
   * POST /resendOTP {challengeToken}
   * 
   * Success (200):
   *   - New OTP sent to email
   *   - Mark as resent (disable button)
   * 
   * Rate Limited (429):
   *   - Too many resends (5 per 24h)
   *   - Contact support
   * 
   * Locked (423):
   *   - Account locked
   *   - Contact support
   * 
   * Expired (401):
   *   - Token expired
   *   - Redirect to login
   */
  const handleResendOTP = async () => {
    if (isLoading || isResending || hasResentOnce || remainingSeconds > 0) {
      return;
    }

    if (!challengeToken) {
      setError(true);
      setErrMsg("Session expired. Please login again.");
      return;
    }

    setIsResending(true);
    setErrMsg("");
    setError(false);

    try {
      /* Call API utility which includes challenge token */
      const response = await resendOtp();

      if (response && response.status === 200) {
        setHasResentOnce(true);
        setErrMsg("");
      } else {
        setError(true);
        setErrMsg(getErrorMessage(response?.status, response?.error));
        
        if (response?.status === 401) {
          setTimeout(() => {
            window.location = '/preLogin';
          }, 2000);
        }
      }
    } catch (err) {
      setError(true);
      setErrMsg("Error resending OTP. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  /**
   * Handle Enter key - submit OTP
   */
  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleVerifyOTP();
    }
  };

  /* Resend button disabled state */
  const resendDisabled = isLoading || isResending || hasResentOnce || remainingSeconds > 0;

  /* Resend button label with countdown */
  let resendLabel = "Resend";
  if (hasResentOnce) {
    resendLabel = "Code sent ✓";
  } else if (remainingSeconds > 0) {
    resendLabel = `Resend (${remainingSeconds}s)`;
  }

  return (
    <div className="two-factor-otp">
      <WelcomeHeader
        title="Welcome to eClosing!"
        subtitle="Sign into eClosing with"
      />

      <div className="otp-container">
        <div className="otp-panel-title">
          <Typography style={{ fontWeight: 'bold' }}>Verify your Identity</Typography>
        </div>

        {isLoading && <CircularProgress size={30} className="loading-spinner" />}

        <form className="form-container" autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <Typography className="otp-subtitle">Enter the 6-digit code sent to your email</Typography>

          {isError && (
            <div className="error-msg">
              <Typography className="msg">{errMsg}</Typography>
            </div>
          )}

          <div className="otp-input-row">
            {otpDigits.map((digit, index) => (
              <input
                key={OTP_INPUT_KEYS[index]}
                ref={(el) => {
                  otpInputRefs.current[index] = el;
                }}
                className="otp-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={1}
                value={digit}
                onChange={(event) => handleOTPChange(index, event)}
                onKeyDown={(event) => handleOTPKeyDown(index, event)}
                onKeyPress={handleKeyPress}
                onPaste={blockOTPClipboardAction}
                disabled={isLoading}
                aria-label={`OTP digit ${index + 1}`}
              />
            ))}
          </div>

          <div className="attempts-info">
            <Typography className="attempts-text">
              {getAttemptsMessage(remainingAttempts)}
            </Typography>
          </div>

          <Button
            color="primary"
            variant="contained"
            className="otp-verify-button"
            onClick={handleVerifyOTP}
            disabled={isLoading || otpCode.length !== OTP_LENGTH}
          >
            {isLoading ? "Verifying..." : "Verify OTP"}
          </Button>

          <div className="otp-links">
            <Typography className="links-text">
              Didn&apos;t receive it?{" "}
              <button
                type="button"
                className="resend-link"
                onClick={handleResendOTP}
                disabled={resendDisabled}
                aria-label="Resend OTP code"
              >
                {resendLabel}
              </button>
            </Typography>

            <Button
              color="default"
              className="back-to-login-button"
              onClick={handleBackToLogin}
              disabled={isLoading}
            >
              Back to Login
            </Button>
          </div>
        </form>

        <div className="otp-info">
          <Typography className="info-text">
            Didn&apos;t receive the code? Check your spam folder or Resend. Still having trouble? Contact support team.
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorOTP;
